import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { widgetDefault } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/BracketingWidgetConfigurationPage/constants'
import configurationServices from '@/configuration/services/services'

import { getSavePreferencesPayload } from './helpers'

const useBracketingWidgetConfiguration = ({ storeId, setToastInfo, supportedLanguages }) => {
  const { t } = useTranslation()
  const { data: bracketingWidgetPreferences, loading: gettingBracketingWidgetPreferences } = useFetch(
    () => configurationServices.getBracketingWidgetPreferences({ storeId }),
    {
      doInitialRequest: true,
    }
  )
  const { fetchData: editBracketingWidgetPreferences, loading: applyingBracketingWidgetPreferences } = useFetch(
    configurationServices.editBracketingWidgetPreferences,
    {
      doInitialRequest: false,
    }
  )
  const [backgroundColor, setBackgroundColor] = useState(widgetDefault.background)
  const [textColor, setTextColor] = useState(widgetDefault.text)
  const [linkColor, setLinkColor] = useState(widgetDefault.link)
  const [customTexts, setCustomTexts] = useState({ title: {}, content: {}, size_guide_url: '' })
  const [useCustomTitle, setUseCustomTitle] = useState(false)
  const [useCustomContent, setUseCustomContent] = useState(false)
  const [useSizeGuide, setUseSizeGuide] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const setInitValues = useCallback(() => {
    if (bracketingWidgetPreferences) {
      setBackgroundColor(bracketingWidgetPreferences?.background_color || widgetDefault.background)
      setTextColor(bracketingWidgetPreferences?.text_color || widgetDefault.text)
      setLinkColor(bracketingWidgetPreferences?.link_color || widgetDefault.link)

      setCustomTexts({
        title: bracketingWidgetPreferences?.title_text || {},
        content: bracketingWidgetPreferences?.content_text || {},
        size_guide_url: bracketingWidgetPreferences?.size_guide_url || '',
      })
      setUseCustomTitle(Object.values(bracketingWidgetPreferences?.title_text).some(value => value !== null))
      setUseCustomContent(Object.values(bracketingWidgetPreferences?.content_text).some(value => value !== null))
      setUseSizeGuide(!!bracketingWidgetPreferences?.size_guide_url)

      setHasChanges(false)
    }
  }, [bracketingWidgetPreferences])

  useEffect(() => {
    setInitValues()
  }, [setInitValues, bracketingWidgetPreferences])

  const revertChanges = () => {
    setInitValues()
  }

  const savePreferences = async () => {
    const payload = getSavePreferencesPayload({
      backgroundColor,
      textColor,
      linkColor,
      useCustomTitle,
      useCustomContent,
      useSizeGuide,
      customTexts,
      supportedLanguages,
    })
    await editBracketingWidgetPreferences({ storeId, preferences: payload })
    setHasChanges(false)
  }

  const scriptInfo = `<div id="reveni-bracketing-widget" data-products='{{cart.items|json}}'></div>
  <script defer="defer" src='${import.meta.env.VITE_REVENI_BRACKETING_WIDGET}?language={{ request.locale.iso_code }}&configuration={{ shop.metafields.reveni.bracketing_widget.value | json }}'></script>`

  const onSave = async () => {
    try {
      await savePreferences()
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, type: 'error', message: error.errorMessage })
    }
  }

  return {
    gettingBracketingWidgetPreferences,
    applyingBracketingWidgetPreferences,
    backgroundColor,
    setBackgroundColor: value => {
      setHasChanges(true)
      setBackgroundColor(value)
    },
    textColor,
    setTextColor: value => {
      setHasChanges(true)
      setTextColor(value)
    },
    linkColor,
    setLinkColor: value => {
      setHasChanges(true)
      setLinkColor(value)
    },
    customTexts,
    setCustomTexts: value => {
      setHasChanges(true)
      setCustomTexts(value)
    },
    useCustomTitle,
    setUseCustomTitle: value => {
      setHasChanges(true)
      setUseCustomTitle(value)
    },
    useCustomContent,
    setUseCustomContent: value => {
      setHasChanges(true)
      setUseCustomContent(value)
    },
    useSizeGuide,
    setUseSizeGuide: value => {
      setHasChanges(true)
      setUseSizeGuide(value)
      setCustomTexts(prev => ({ ...prev, size_guide_url: value ? prev.size_guide_url : '' }))
    },
    hasChanges,
    revertChanges,
    scriptInfo,
    onSave,
  }
}

export default useBracketingWidgetConfiguration
